import { useState } from 'react';
import { useHotkeyScope, type HotkeyBinding } from '@/shared/lib/hotkeys';

export interface UseSelectableRowOptions<T> {
  /** Открыть строку (то же самое действие, что кнопка «Открыть»/двойной клик) — вызывается по F2. */
  onOpen: (item: T) => void;
  /** Главное действие «Новый»/«Создать» страницы — вызывается по Insert. Страницы без создания
   *  «на этом экране» (например, список заявок клиентов) этот параметр не передают. */
  onCreate?: () => void;
  /** Название действия создания для подсказки внизу — по умолчанию «создать». */
  createLabel?: string;
}

/**
 * Общий паттерн списочных страниц: клик по строке выделяет её (для F2), двойной клик/кнопка
 * «Открыть» открывает; плюс горячие клавиши в 1С-стиле — Insert (главное действие создания), F2
 * (открыть выделенную строку), см. CLAUDE.md. Возвращает всё нужное для antd `Table`: `rowProps`
 * для `onRow` и `bindings` — передать в `<HotkeyHint>`. Визуальная подсветка выделенной строки —
 * отдельная задача, реализуется позже.
 */
export function useSelectableRow<T extends { id: string }>(
  items: T[],
  { onOpen, onCreate, createLabel = 'создать' }: UseSelectableRowOptions<T>,
  /** `false` — не регистрировать горячие клавиши (например, неактивная вкладка `Tabs`, где
   *  панели остаются смонтированными, но взаимодействовать с ними сейчас нельзя). */
  enabled = true,
) {
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const selected = items.find((i) => i.id === selectedId);

  const bindings: HotkeyBinding[] = [];
  if (onCreate) bindings.push({ combo: 'Insert', description: createLabel, handler: onCreate });
  if (selected) bindings.push({ combo: 'F2', description: 'открыть выделенную строку', handler: () => onOpen(selected) });
  useHotkeyScope(bindings, enabled);

  return {
    selectedId,
    setSelectedId,
    selected,
    rowProps: (record: T) => ({
      onClick: () => setSelectedId(record.id),
      onDoubleClick: () => onOpen(record),
    }),
    bindings,
  };
}
